import type {
  AdminSelectAgendaRequest,
  AdminSelectAgendaResponse,
  AdminSelectCollectionPlayerRequest,
  AdminSelectCollectionPlayerResponse,
  AdminSelectContentRequest,
  AdminSelectContentResponse,
} from '@sdk/api/modules/admin/types.admin'
import { ADMIN_API_METHOD_TYPES } from '@sdk/api/modules/admin/types.admin'
import { usePostRobot } from '@sdk/utils/postrobot'

/**
 * Opens the collection player selector modal to allow the user to select collection player content and handle the outcome.
 * ```
 * const api = useApi() // or useAdmin() if you know that you 're in Admin context
 *
 * adminApi.selectCollectionContent({
 *  // optional payload
 *  initial_data: {
 *   name: 'test',
 *   groups: []
 *  }
 * })
 * ```
 */
export async function selectCollectionContent(
  payload: AdminSelectCollectionPlayerRequest = {},
): Promise<AdminSelectCollectionPlayerResponse> {
  const pr = await usePostRobot()
  const response = await pr.send(window.parent, ADMIN_API_METHOD_TYPES.SELECT_COLLECTION_PLAYER_CONTENT, payload)

  return <AdminSelectCollectionPlayerResponse>response.data
}

/**
 * Opens the agenda selector modal to allow the user to select agenda content and handle the outcome.
 * ```
 * const api = useApi() // or useAdmin() if you know that you 're in Admin context
 *
 * adminApi.selectAgendaContent({
 *  // optional payload
 *  initial_agenda: {
 *   name: 'test',
 *   groups: []
 *  }
 * })
 * ```
 */
export async function selectAgendaContent(payload: AdminSelectAgendaRequest = {}): Promise<AdminSelectAgendaResponse> {
  const pr = await usePostRobot()
  const response = await pr.send(window.parent, ADMIN_API_METHOD_TYPES.SELECT_AGENDA_CONTENT, payload)

  return <AdminSelectAgendaResponse>response.data
}

/**
 * Opens a selector that allows you to select (and preselect) from your Pitcher uploaded content.
 *
 * @example
 * const api = useApi() // or useAdmin() if you know that you 're in Admin app context
 * api
 *   .select_content({
 *     selections: [
 *       { fileId: "01HCZ623YYRFJQ0F7B69VWE510", type: "file" },
 *       {
 *         fileId: "01HD3XGZ5FN90QJGK7CH8TBZDF",
 *         type: "page",
 *         pageIndex: 2,
 *       },
 *     ],
 *   })
 */
export async function selectContent(payload: AdminSelectContentRequest): Promise<AdminSelectContentResponse> {
  const pr = await usePostRobot()
  const response = await pr.send(window.parent, ADMIN_API_METHOD_TYPES.SELECT_CONTENT, payload)
  return response.data as AdminSelectContentResponse
}
